import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';

import { useTranslations } from '@lang';
import { Button } from '@common/components';
import { ReduxFormInput } from '@common/components/FormElements/BlockInputField';
import { RadioGroupFlexField } from '@common/components/RadioGroupFlexField';
import { Spinner } from '@common/components/Spinner';
import { FormRow, FormSuccessMessage } from '@common/components/AuthLayout/Layout';

import { required, validEmail, validNumber } from '@utils/validators';
import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import { InfoIcon } from '@root/styles/icons';
import { Popover } from 'react-tiny-popover';

import { Form, InlineWrapper, PopoverMessage } from './RegisterForm.styles';

const validate = (values) => {
  const errors = {};

  errors.email = required(values.email) || validEmail(values.email);
  errors.alreadySpaMember = required(values.alreadySpaMember);
  errors.externalSpaId = required(values.externalSpaId) || validNumber(values.externalSpaId);

  return errors;
};

const radioOptions = [
  { label: 'Yes', value: 'yes', id: 'register.yes' },
  { label: 'No', value: 'no', id: 'register.no' },
];

const RegisterForm = ({
  handleSubmit,
  submitting,
  submitSucceeded,
  spaIntegrated = false,
  resetSection,
}) => {
  const { t } = useTranslations();
  const [isSpaMember, setIsSpaMember] = useState('');

  const handleIsSpaMemberChange = (value) => {
    setIsSpaMember(value);
    if (value === 'no') resetSection('registerForm', 'externalSpaId');
  };

  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const togglePopoverOpen = () => setPopoverIsOpen(!popoverIsOpen);

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Spinner isActive={submitting} showDelayMessage />
      {!submitSucceeded && (
        <>
          <FormRow>
            <Field
              component={ReduxFormInput}
              name="email"
              label={t('register.emailLabel')}
              isRequired
            />
          </FormRow>
          {spaIntegrated && (
            <FormRow>
              <Field
                component={RadioGroupFlexField}
                options={radioOptions}
                name="alreadySpaMember"
                onChange={handleIsSpaMemberChange}
                label={t('register.alreadySpaMember')}
                isRequired
              />
            </FormRow>
          )}
          {spaIntegrated && isSpaMember === 'yes' && (
            <FormRow>
              <Field
                component={ReduxFormInput}
                name="externalSpaId"
                label={
                  <InlineWrapper>
                    {t('register.uniqueId')}
                    <Popover
                      isOpen={popoverIsOpen}
                      onClickOutside={() => setPopoverIsOpen(false)}
                      content={<PopoverMessage>{t('register.popoverMessage')}</PopoverMessage>}
                    >
                      <InfoIcon
                        onClick={togglePopoverOpen}
                        cursor="pointer"
                        width="16"
                        height="16"
                      />
                    </Popover>
                  </InlineWrapper>
                }
                isRequired
              />
            </FormRow>
          )}
          <FormRow>
            <Button type="submit" disabled={submitting} isPrimary>
              {t('register.register')}
            </Button>
          </FormRow>
        </>
      )}
      {submitSucceeded && (
        <>
          <FormRow>
            <FormSuccessMessage>{t('register.successMessage')}</FormSuccessMessage>
          </FormRow>
          <FormRow>
            <Link route={Routes.HOME}>
              <Button disabled={submitting}>{t('register.backToHomepage')}</Button>
            </Link>
          </FormRow>
        </>
      )}
    </Form>
  );
};

RegisterForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  spaIntegrated: PropTypes.bool,
};

export default reduxForm({
  form: 'registerForm',
  validate,
  touchOnChange: true,
})(RegisterForm);
